import { AI_AGENTS_INFO, TOP_BAR_COMMON_SX } from './aiAgentsConsts';
import { ReactNode } from 'react';
import { Box, Stack } from '@mui/material';
import { colors, Typography } from '@sweep-io/sweep-design';
import useAiAgents from './useAiAgents';
import AgentCard from './AgentCard';
import { CenteredCircularProgress } from '../common/CenteredCircularProgress';
import usePermission from '../common/permissions/usePermission';
import { AgentType } from '@server/ai';
import ToggleExpandButton from './ToggleExpandButton';
import NewAgentButton from './NewAgentButton';
import usePopover from '../../hooks/usePopover';
import { useFeatureToggle } from '../common/useFeatureToggle';
import useAiAgentChats from './useAiAgentChats';

const AgentsCardsCollapsed = ({
  closeButton,
  onNewAgentClick,
}: {
  closeButton: ReactNode;
  onNewAgentClick: (type: AgentType) => void;
}) => {
  const { startChatWithAgent } = useAiAgentChats();
  const { agents, agentsIsLoading } = useAiAgents();
  const [isAllowedCreate] = usePermission(['create:ai-agent']);
  const menuPopoverProps = usePopover();
  const {
    documentationAiAgent,
    aiAgentCenter: aiProcessAgent,
    monitoringAiAgent,
  } = useFeatureToggle();

  const agentsToRender = documentationAiAgent
    ? agents
    : agents?.filter((agent) => agent.type !== AgentType.Documentation);

  const allowedCreateAtLeastOneType = aiProcessAgent || monitoringAiAgent;
  const showNewAgentsButton = isAllowedCreate && allowedCreateAtLeastOneType;

  return (
    <Stack height="100%" sx={{ backgroundColor: '#ECF8FF' }}>
      <Box
        sx={{
          borderBottom: `1px solid ${colors.grey[300]}`,
          justifyContent: 'flex-end',
          ...TOP_BAR_COMMON_SX,
        }}
      >
        <ToggleExpandButton />
        {closeButton}
      </Box>

      <Stack
        sx={{
          overflow: 'hidden',
        }}
      >
        <Stack
          p="24px 16px"
          height="100%"
          gap={3}
          sx={{
            overflow: 'auto',
          }}
        >
          <Stack
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              gap: '16px',
              alignItems: 'start',
            }}
          >
            <Typography variant="h2" color={colors.grey[800]}>
              Agentic Center
            </Typography>
            {showNewAgentsButton && (
              <Stack minWidth="fit-content">
                <NewAgentButton onNewAgentClick={onNewAgentClick} {...menuPopoverProps} />
              </Stack>
            )}
          </Stack>
          {agentsIsLoading && (
            <Stack pt={5} flex={1}>
              <CenteredCircularProgress />
            </Stack>
          )}

          {!agentsIsLoading && (
            <Stack gap={3}>
              {agentsToRender?.map((agent) => (
                <AgentCard
                  key={agent.id}
                  agentName={agent.name}
                  agentType={agent.type}
                  onClick={() =>
                    startChatWithAgent({
                      agent,
                      biEventProps: {
                        origin: 'agent_card',
                        mode: 'collapsed',
                      },
                    })
                  }
                  opacity={menuPopoverProps.isPopoverOpen ? 0.6 : 1}
                />
              ))}
            </Stack>
          )}
          {!documentationAiAgent && (
            <AgentCard
              key={AgentType.Documentation}
              agentName={AI_AGENTS_INFO[AgentType.Documentation].nameForComingSoon}
              agentType={AgentType.Documentation}
              isRenderComingSoon={true}
            />
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default AgentsCardsCollapsed;
